/* eslint-disable @typescript-eslint/naming-convention */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  Output,
  ViewChild,
} from '@angular/core';
import { SwitchSize, InputType } from '@ansys-internal/awc-angular/forms';
import { ConceptInputType } from 'src/app/shared/enums/concept.enum';
import { ConceptInputComponent } from '../forms/input/concept-input.component';
import { UnitType } from 'src/api';
import { UploadFileResponseType } from '../../../shared/types/upload-file-response.type';
// import { AppModule } from '../../../app.module';
import { BatteryComponent } from './inputs/battery/battery.component';
import { CommonModule, NgSwitch } from '@angular/common';
import { DriveCyclesComponent } from './inputs/drive-cycles/drive-cycles.component';
import { DynamicRequirementsComponent } from './inputs/dynamic-requirements/dynamic-requirements.component';
import { StaticRequirementsComponent } from './inputs/static-requirements/static-requirements.component';
import { InverterComponent } from './inputs/inverter/inverter.component';
import { MotorComponent } from './inputs/motor/motor.component';
import { TransmissionComponent } from './inputs/transmission/transmission.component';
import { ClutchComponent } from './inputs/clutch/clutch.component';
import { WheelsComponent } from './inputs/wheels/wheels.component';
import { MassComponent } from './inputs/mass/mass.component';
import { AerodynamicsComponent } from './inputs/aerodynamics/aerodynamics.component';

@Component({
    selector: 'app-part-inputs',
    templateUrl: './part-inputs.component.html',
    styleUrls: ['./part-inputs.component.scss'],
    imports: [
        CommonModule,
        BatteryComponent,
        ConceptInputComponent,
        DriveCyclesComponent,
        DynamicRequirementsComponent,
        StaticRequirementsComponent,
        InverterComponent,
        MotorComponent,
        TransmissionComponent,
        ClutchComponent,
        WheelsComponent,
        MassComponent,
        AerodynamicsComponent,
    ]
})
export class PartInputsComponent {
  @Input() public part!: any;
  @Input() public thermalFile!: File;
  @Output() public thermalFileChange: EventEmitter<any> =
    new EventEmitter<any>();
  @Input() public type!: string;
  @Input() public fileResponse?: UploadFileResponseType;
  @Input() public forceEnable!: boolean;
  @Input() public update: boolean = false;
  @Input() readonly: boolean = false;
  @Output() public partChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() public updateInput: EventEmitter<string> =
    new EventEmitter<string>();
  @Output() partsLoaded: EventEmitter<boolean> = new EventEmitter(true);
  @ViewChild('fileUpload') upload!: ElementRef;
  protected partsLoadedEmitted: boolean = true;
  protected readonly InputTypes = InputType;
  protected readonly ConceptInputType = ConceptInputType;
  protected readonly UnitType = UnitType;
  protected size: SwitchSize = SwitchSize.MEDIUM;

  constructor(private el: ElementRef, private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    setTimeout(() => this.resetScrollToTop(), 0);
  }

  private resetScrollToTop(): void {
    const firstChild = (this.el.nativeElement as HTMLElement).querySelector(
      'app-input'
    ) as HTMLElement | null;
    const scrollable = this.findScrollableParent(
      firstChild ?? (this.el.nativeElement as HTMLElement)
    );
    if (scrollable) {
      this.ngZone.runOutsideAngular(() => {
        scrollable.scrollTop = 0;
      });
    } else {
      window.scrollTo(0, 0);
    }
  }

  private findScrollableParent(el: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = el;
    while (current) {
      const style = getComputedStyle(current);
      const overflowY = style.overflowY;
      if (
        (overflowY === 'auto' ||
          overflowY === 'scroll' ||
          overflowY === 'overlay') &&
        current.scrollHeight > current.clientHeight
      ) {
        return current;
      }
      current = current.parentElement;
    }

    return (document.scrollingElement ??
      document.documentElement) as HTMLElement;
  }

  /**
   * Set the part value
   */
  protected setPart(
    value: string | number | null,
    type: string,
    id: string,
    index?: number
  ): void {
    if (index !== undefined) {
      this.part[type][index] = value;
      this.partChange.emit(this.part);
      this.update && id && this.updateInput.emit(id);
      return;
    }

    if (typeof value === 'string' && value.length === 0) {
      value = null;
    }

    this.setDeep(this.part, type, value);

    this.partChange.emit(this.part);
    this.update && id && this.updateInput.emit(id);
  }


  private setDeep(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];

      // Create nested objects if missing
      if (current[key] === undefined || current[key] === null) {
        current[key] = {};
      }

      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
  }

  protected forceUpdate(input: string): void {
    this.partChange.emit(this.part);
    this.thermalFile && this.thermalFileChange.emit(this.thermalFile);
    this.update && this.updateInput.emit(input);
  }
}
